import React, { useState, useRef } from 'react';
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { 
  PlusIcon, 
  TrashIcon, 
  ArrowUpIcon, 
  ArrowDownIcon,
  CloudArrowUpIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { useExperiences } from '../../contexts/ExperienceContext';
import { Experience, uploadCompanyLogo } from '../../firebase/experienceService';
import { openCloudinaryUploadWidget } from '../../services/cloudinaryService';

const ExperienceSchema = Yup.object().shape({
  company: Yup.string()
    .required('Company name is required')
    .max(100, 'Company name is too long'),
  position: Yup.string()
    .required('Position is required')
    .max(100, 'Position title is too long'),
  location: Yup.string()
    .max(100, 'Location is too long'),
  startDate: Yup.string()
    .required('Start date is required')
    .matches(/^\d{4}-\d{2}$/, 'Format must be YYYY-MM'),
  endDate: Yup.string()
    .matches(/^\d{4}-\d{2}$/, 'Format must be YYYY-MM')
    .nullable(),
  website: Yup.string()
    .url('Please enter a valid URL')
    .nullable(),
  descriptions: Yup.array()
    .of(Yup.string().required('Description is required'))
    .min(1, 'At least one description is required')
});

const ExperienceEditor: React.FC = () => {
  const { experiences, loading, addExperience, editExperience, removeExperience, moveUp, moveDown } = useExperiences();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [selectedExperience, setSelectedExperience] = useState<Experience | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState<boolean>(false);
  const [logoProgress, setLogoProgress] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const initialValues: Omit<Experience, 'id' | 'createdAt' | 'updatedAt'> = {
    company: '',
    position: '',
    location: '',
    startDate: '',
    endDate: '',
    descriptions: [''],
    companyLogo: '',
    website: ''
  };
  
  const handleCancel = () => {
    setEditMode(false);
    setSelectedExperience(null);
  };
  
  const handleEdit = (experience: Experience) => {
    setSelectedExperience(experience);
    setEditMode(true);
  };
  
  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this experience?')) return;
    
    try {
      await removeExperience(id);
      toast.success('Experience deleted successfully');
    } catch (error) {
      toast.error('Failed to delete experience');
      console.error('Error deleting experience:', error);
    }
  };

  const handleMoveUp = async (id: string) => {
    try {
      await moveUp(id);
    } catch (error) {
      toast.error('Failed to reorder experiences');
      console.error('Error reordering experiences:', error);
    }
  };

  const handleMoveDown = async (id: string) => {
    try {
      await moveDown(id);
    } catch (error) {
      toast.error('Failed to reorder experiences');
      console.error('Error reordering experiences:', error);
    }
  };
  
  const handleSubmit = async (values: any, { resetForm, setFieldValue }: any) => {
    try {
      if (selectedExperience?.id) {
        await editExperience(selectedExperience.id, values);
        toast.success('Experience updated successfully');
      } else {
        await addExperience(values);
        toast.success('Experience added successfully');
      }
      
      resetForm();
      setEditMode(false);
      setSelectedExperience(null);
    } catch (error) {
      toast.error('Failed to save experience');
      console.error('Error saving experience:', error);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Experience Editor</h1>
        {!editMode && (
          <button
            type="button"
            className="btn-primary flex items-center"
            onClick={() => setEditMode(true)}
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add Experience
          </button>
        )}
      </div>
      
      {editMode ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">
            {selectedExperience ? 'Edit Experience' : 'Add New Experience'}
          </h2>
          
          <Formik
            initialValues={selectedExperience || initialValues}
            validationSchema={ExperienceSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, setFieldValue, isSubmitting }) => {
              // Logo upload handler with access to Formik's setFieldValue
              const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
                if (e.target.files && e.target.files[0]) {
                  const file = e.target.files[0];
                  
                  try {
                    setUploadingLogo(true);
                    setLogoProgress(0);
                    
                    const imageUrl = await uploadCompanyLogo(file, (progress) => {
                      setLogoProgress(progress);
                    });
                    
                    // Set the logo URL in form values
                    setFieldValue('companyLogo', imageUrl);
                    
                    // If editing an existing experience, update it in the database
                    if (selectedExperience?.id) {
                      await editExperience(selectedExperience.id, { companyLogo: imageUrl });
                      setSelectedExperience({...selectedExperience, companyLogo: imageUrl});
                    }
                    
                    toast.success('Logo uploaded successfully');
                  } catch (error) {
                    toast.error('Failed to upload logo');
                    console.error('Error uploading logo:', error);
                  } finally {
                    setUploadingLogo(false);
                    setLogoProgress(0);
                  }
                }
              };
              
              // Cloudinary widget handler with access to Formik's setFieldValue
              const handleCloudinaryWidget = () => {
                openCloudinaryUploadWidget({
                  folder: 'portfolio_experiences',
                  multiple: false,
                  maxFiles: 1,
                  resourceType: 'image',
                  onSuccess: async (urls) => {
                    if (urls && urls.length > 0) {
                      const imageUrl = urls[0];
                      
                      // Set the logo URL in form values
                      setFieldValue('companyLogo', imageUrl);
                      
                      // If editing an existing experience, update it in the database
                      if (selectedExperience?.id) {
                        await editExperience(selectedExperience.id, { companyLogo: imageUrl });
                        setSelectedExperience({...selectedExperience, companyLogo: imageUrl});
                      }
                      
                      toast.success('Logo uploaded successfully');
                    }
                  },
                  onError: (error) => {
                    toast.error('Failed to upload logo');
                    console.error('Error uploading with Cloudinary widget:', error);
                  }
                });
              };
              
              return (
                <Form className="space-y-6">
                  <input 
                    type="hidden" 
                    id="companyLogo" 
                    name="companyLogo" 
                    value={values.companyLogo || ''} 
                  />
                  
                  {/* Logo Upload Section */}
                  <div className="mb-6 p-4 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                    <div className="flex flex-col sm:flex-row items-center gap-4">
                      <div className="w-20 h-20 flex-shrink-0 bg-gray-100 dark:bg-gray-700 rounded-md flex items-center justify-center overflow-hidden relative group">
                        {values.companyLogo ? (
                          <>
                            <img 
                              src={values.companyLogo} 
                              alt="Company logo" 
                              className="w-full h-full object-contain"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-md flex items-center justify-center">
                              <button 
                                type="button"
                                className="text-white p-1"
                                onClick={() => {
                                  setFieldValue('companyLogo', '');
                                  if (selectedExperience?.id) {
                                    editExperience(selectedExperience.id, { companyLogo: '' });
                                  }
                                }}
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            </div>
                          </>
                        ) : (
                          <ExclamationCircleIcon className="w-10 h-10 text-gray-400" />
                        )}
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="font-medium mb-2">Company Logo</h3>
                        <p className="text-sm text-gray-500 mb-3">
                          Upload a logo for the company. Recommended size: 200x200px
                        </p>
                        
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            Upload from device
                          </button>
                          
                          <button
                            type="button"
                            className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                            onClick={handleCloudinaryWidget}
                          >
                            Choose from Media Library
                          </button>
                          
                          <input
                            ref={fileInputRef}
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={handleLogoUpload}
                          />
                        </div>
                        
                        {uploadingLogo && (
                          <div className="mt-2">
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                              <div 
                                className="bg-primary-600 h-2.5 rounded-full" 
                                style={{ width: `${logoProgress}%` }}
                              ></div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Uploading: {logoProgress}%</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Company Name */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="company">
                        Company Name*
                      </label>
                      <Field
                        id="company"
                        name="company"
                        type="text"
                        className="form-input"
                        placeholder="Company name"
                      />
                      <ErrorMessage name="company" component="p" className="text-red-500 text-xs mt-1" />
                    </div>
                    
                    {/* Position */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="position">
                        Position Title*
                      </label>
                      <Field
                        id="position"
                        name="position"
                        type="text"
                        className="form-input"
                        placeholder="Your position/title"
                      />
                      <ErrorMessage name="position" component="p" className="text-red-500 text-xs mt-1" />
                    </div>
                    
                    {/* Location */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="location">
                        Location
                      </label>
                      <Field
                        id="location"
                        name="location"
                        type="text"
                        className="form-input"
                        placeholder="City, Country"
                      />
                      <ErrorMessage name="location" component="p" className="text-red-500 text-xs mt-1" />
                    </div>
                    
                    {/* Website */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="website">
                        Company Website
                      </label>
                      <Field
                        id="website"
                        name="website"
                        type="url"
                        className="form-input"
                        placeholder="https://example.com"
                      />
                      <ErrorMessage name="website" component="p" className="text-red-500 text-xs mt-1" />
                    </div>
                    
                    {/* Start Date */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="startDate">
                        Start Date* (YYYY-MM)
                      </label>
                      <Field
                        id="startDate"
                        name="startDate"
                        type="text"
                        className="form-input"
                        placeholder="2022-01"
                      />
                      <ErrorMessage name="startDate" component="p" className="text-red-500 text-xs mt-1" />
                    </div>
                    
                    {/* End Date */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="endDate">
                        End Date (YYYY-MM, leave empty for 'Present')
                      </label>
                      <Field
                        id="endDate"
                        name="endDate"
                        type="text"
                        className="form-input"
                        placeholder="2024-01"
                      />
                      <ErrorMessage name="endDate" component="p" className="text-red-500 text-xs mt-1" />
                    </div>
                  </div>
                  
                  {/* Descriptions */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Description/Responsibilities*
                    </label>
                    <FieldArray name="descriptions">
                      {({ remove, push }) => (
                        <div className="space-y-3">
                          {values.descriptions.map((desc, index) => (
                            <div key={index} className="flex items-start gap-2">
                              <Field
                                name={`descriptions.${index}`}
                                as="textarea"
                                rows={2}
                                className="form-textarea flex-1"
                                placeholder="Describe your responsibilities, achievements, or contributions"
                              />
                              <button
                                type="button"
                                className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900 rounded-md"
                                onClick={() => {
                                  if (values.descriptions.length > 1) {
                                    remove(index);
                                  } else {
                                    toast.info('At least one description is required');
                                  }
                                }}
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            </div>
                          ))}
                          <ErrorMessage name="descriptions" component="p" className="text-red-500 text-xs mt-1" />
                          
                          <div>
                            <button
                              type="button"
                              className="px-4 py-2 text-sm text-primary-600 border border-primary-600 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900 flex items-center"
                              onClick={() => push('')}
                            >
                              <PlusIcon className="w-4 h-4 mr-1" />
                              Add Another Bullet Point
                            </button>
                          </div>
                        </div>
                      )}
                    </FieldArray>
                  </div>
                  
                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={handleCancel}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Saving...' : 'Save Experience'}
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      ) : (
        <div className="space-y-4">
          {experiences.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                You haven't added any experiences yet.
              </p>
              <button
                type="button"
                className="btn-primary inline-flex items-center"
                onClick={() => setEditMode(true)}
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Add Your First Experience
              </button>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {experiences.map((exp) => (
                  <li key={exp.id} className="group">
                    <div className="p-4 sm:p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4 flex-1">
                          <div className="w-12 h-12 flex-shrink-0 bg-gray-100 dark:bg-gray-700 rounded-md flex items-center justify-center overflow-hidden">
                            {exp.companyLogo ? (
                              <img 
                                src={exp.companyLogo} 
                                alt={`${exp.company} logo`} 
                                className="w-full h-full object-contain"
                              />
                            ) : (
                              <span className="text-gray-400 text-sm text-center">{exp.company.charAt(0)}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                              {exp.position}
                            </h3>
                            <div className="flex items-center mt-1">
                              <p className="text-base text-gray-700 dark:text-gray-300">{exp.company}</p>
                              {exp.location && (
                                <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                                  • {exp.location}
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-sm text-primary-600 dark:text-primary-400">
                              {exp.startDate ? (
                                <>
                                  {exp.startDate} - {exp.endDate || 'Present'}
                                </>
                              ) : 'No date specified'}
                            </p>
                            <div className="mt-2">
                              <ul className="list-disc pl-5 space-y-1">
                                {exp.descriptions.map((desc, idx) => (
                                  <li key={idx} className="text-sm text-gray-600 dark:text-gray-400">
                                    {desc}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            type="button"
                            className="p-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700"
                            title="Move up"
                            onClick={() => exp.id && handleMoveUp(exp.id)}
                          >
                            <ArrowUpIcon className="w-5 h-5" />
                          </button>
                          <button
                            type="button"
                            className="p-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700"
                            title="Move down"
                            onClick={() => exp.id && handleMoveDown(exp.id)}
                          >
                            <ArrowDownIcon className="w-5 h-5" />
                          </button>
                          <button
                            type="button"
                            className="p-1 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900 text-blue-600 dark:text-blue-400"
                            title="Edit"
                            onClick={() => handleEdit(exp)}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="p-1 rounded-md hover:bg-red-100 dark:hover:bg-red-900 text-red-600 dark:text-red-400"
                            title="Delete"
                            onClick={() => exp.id && handleDelete(exp.id)}
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExperienceEditor; 